import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useContactOrganizations, useContactProperties, useContactClaims } from "@/hooks/use-contacts";
import { LinkEntityPopover, LinkedEntityRow } from "../LinkEntityPopover";
import type { LinkableEntity } from "../LinkEntityPopover";
import { navigateDirectory } from "../navigate";
import { apiClient } from "@/lib/api-client";
import { Building2, MapPin, Phone, Mail, Briefcase, FileText, Tag, User, StickyNote } from "lucide-react";
import { useLocation } from "wouter";
import type { Contact } from "@/api/generated/client/models";

interface ContactDrawerProps {
    contact: Contact | null;
    open: boolean;
    onClose: () => void;
    onEdit: (contact: Contact) => void;
}

export function ContactDrawer({ contact, open, onClose, onEdit }: ContactDrawerProps) {
    const [, setLocation] = useLocation();
    const { contactOrganizations, isLoading: orgsLoading, linkOrganization, unlinkOrganization } = useContactOrganizations(contact?.id ?? null);
    const { contactProperties, isLoading: propsLoading, linkProperty, unlinkProperty } = useContactProperties(contact?.id ?? null);
    const { contactClaims, isLoading: claimsLoading, linkClaim, unlinkClaim } = useContactClaims(contact?.id ?? null, contact?.contactType);

    const searchOrganizations = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.organizations.listOrganizations({ search: query || undefined, limit: 15 });
        return (res.organizations || []).map((o) => ({ id: o.id, label: o.name || `Org #${o.id}`, badge: o.orgType }));
    }, []);

    const searchProperties = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.properties.listProperties({ search: query || undefined, limit: 15 });
        return (res.properties || []).map((p) => ({
            id: p.id,
            label: [p.streetAddress, p.unit, p.city, p.state, p.zipCode].filter(Boolean).join(", "),
        }));
    }, []);

    const searchClaims = useCallback(async (query: string): Promise<LinkableEntity[]> => {
        const res = await apiClient.claims.listClaims({ search: query || undefined, limit: 15 });
        return (res.claims || [])
            // Only allow linking claims the user can edit; read-only shares are hidden.
            .filter((c) => (c.canWrite ?? false))
            .map((c) => ({
                id: c.id,
                label: [c.insuredName, c.claimNumber].filter(Boolean).join(" – ") || `Claim #${c.id}`,
            }));
    }, []);

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="overflow-y-auto overflow-x-hidden sm:max-w-lg">
                {contact && (
                    <>
                        <SheetHeader className="pb-4">
                            <SheetTitle className="text-xl flex items-center gap-2 break-words min-w-0">
                                <User className="h-5 w-5 text-muted-foreground shrink-0" />
                                <span className="break-words min-w-0">{contact.firstName} {contact.lastName}</span>
                            </SheetTitle>
                            <SheetDescription className="flex items-center gap-2 flex-wrap">
                                {contact.contactType && (
                                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                        <Tag className="h-3.5 w-3.5 shrink-0" />{contact.contactType}
                                    </span>
                                )}
                            </SheetDescription>
                        </SheetHeader>

                        {/* Contact Info */}
                        <div className="space-y-3 pb-4">
                            {contact.phone && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Phone className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{contact.phone}</span>
                                </div>
                            )}
                            {contact.email && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{contact.email}</span>
                                </div>
                            )}
                            {contact.jobTitle && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{contact.jobTitle}</span>
                                </div>
                            )}
                            {contact.notes && (
                                <div className="flex items-start gap-2 text-sm min-w-0">
                                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="break-words min-w-0">{contact.notes}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 pb-4">
                            <Button variant="outline" size="sm" onClick={() => onEdit(contact)}>
                                Edit Contact
                            </Button>
                        </div>

                        <Separator />

                        {/* Organizations Section */}
                        <div className="py-4">
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h3 className="text-sm font-medium flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    Organizations ({contactOrganizations.length})
                                </h3>
                                <LinkEntityPopover
                                    existingIds={contactOrganizations.map(co => co.organizationId)}
                                    onSearch={searchOrganizations}
                                    onSelect={(id) => linkOrganization({ organizationId: id })}
                                    placeholder="Search organizations..."
                                    emptyMessage="No matching organizations"
                                    noItemsMessage="No organizations yet"
                                    icon={Building2}
                                />
                            </div>
                            {orgsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-full" />
                                </div>
                            ) : contactOrganizations.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No linked organizations</p>
                            ) : (
                                <div className="space-y-2">
                                    {contactOrganizations.map((co) => (
                                        <LinkedEntityRow
                                            key={co.id}
                                            icon={Building2}
                                            label={co.organizationName || `Org #${co.organizationId}`}
                                            onNavigate={() => { onClose(); navigateDirectory("organizations", co.organizationId); }}
                                            onUnlink={() => unlinkOrganization(co.organizationId)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator />

                        {/* Properties Section */}
                        <div className="py-4">
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h3 className="text-sm font-medium flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Properties ({contactProperties.length})
                                </h3>
                                <LinkEntityPopover
                                    existingIds={contactProperties.map(cp => cp.propertyId)}
                                    onSearch={searchProperties}
                                    onSelect={(id) => linkProperty({ propertyId: id })}
                                    placeholder="Search properties..."
                                    emptyMessage="No matching properties"
                                    noItemsMessage="No properties yet"
                                    icon={MapPin}
                                />
                            </div>
                            {propsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-10 w-full" />
                                </div>
                            ) : contactProperties.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No linked properties</p>
                            ) : (
                                <div className="space-y-2">
                                    {contactProperties.map((cp) => (
                                        <LinkedEntityRow
                                            key={cp.id}
                                            icon={MapPin}
                                            label={[cp.streetAddress, cp.city, cp.state, cp.zipCode].filter(Boolean).join(", ") || `Property #${cp.propertyId}`}
                                            onUnlink={() => unlinkProperty(cp.propertyId)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Separator />

                        {/* Claims Section */}
                        <div className="py-4">
                            <div className="flex items-center justify-between flex-wrap mb-3">
                                <h3 className="text-sm font-medium flex items-center gap-2">
                                    <FileText className="h-4 w-4" />
                                    Claims ({contactClaims.length})
                                </h3>
                                <LinkEntityPopover
                                    existingIds={contactClaims.map((cc) => cc.claimId)}
                                    onSearch={searchClaims}
                                    onSelect={(id) => linkClaim(id)}
                                    placeholder="Search claims..."
                                    emptyMessage="No matching claims"
                                    noItemsMessage="No claims yet"
                                    icon={FileText}
                                />
                            </div>
                            {claimsLoading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-10 w-full" />
                                </div>
                            ) : contactClaims.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No linked claims</p>
                            ) : (
                                <div className="space-y-2">
                                    {contactClaims.map((cc) => (
                                        <LinkedEntityRow
                                            key={cc.id}
                                            icon={FileText}
                                            label={[cc.insuredName, cc.claimNumber].filter(Boolean).join(" – ") || `Claim #${cc.claimId}`}
                                            onNavigate={() => { onClose(); setLocation(`/claims/${cc.claimId}`); }}
                                            onUnlink={() => unlinkClaim(cc.claimId)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
